/**
 * Utility functions for handling optional question data with backward compatibility
 * for the "answered" property pattern.
 */

/**
 * Checks if an optional question has been explicitly answered by the user.
 * This function provides backward compatibility between the old value-based format
 * and the new format that includes an explicit "answered" property.
 * 
 * @param {Object} questionData - The question data object
 * @param {any} questionData.value - The answer value
 * @param {string} questionData.notes - Optional notes
 * @param {boolean} questionData.answered - Explicit answered flag (may not exist in old data)
 * @returns {boolean} True if the question has been explicitly answered
 */
export const isQuestionAnswered = (questionData) => {
  if (!questionData) return false;

  // New format: Use explicit answered property if available
  if (questionData.answered !== undefined) {
    return questionData.answered === true;
  }

  // Old format: Fall back to value-based logic for backward compatibility
  return isValueBasedAnswered(questionData);
};

/**
 * Legacy logic for determining if a question is answered based on its value.
 * Used for backward compatibility with data that doesn't have the "answered" property.
 * 
 * @param {Object} questionData - The question data object
 * @returns {boolean} True if the value indicates the question was answered
 */
const isValueBasedAnswered = (questionData) => {
  if (!questionData || questionData.value === null || questionData.value === undefined) {
    return false;
  }

  const { value } = questionData;

  // For string values (text inputs, multiline)
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  // For numeric values
  if (typeof value === 'number') {
    // For yes/no questions (boolean converted to number), any explicit value counts as answered
    // For other numeric questions, positive values indicate answered
    return value > 0;
  }

  // For boolean values (yes/no questions)
  if (typeof value === 'boolean') {
    // In the old format, both true and false could indicate answered
    // But this is ambiguous, which is why the "answered" property was added
    return true;
  }

  return false;
};

/**
 * Checks if an optional question has meaningful content (not just default/empty).
 * This is different from isQuestionAnswered - it checks the quality of the answer.
 * 
 * @param {Object} questionData - The question data object
 * @returns {boolean} True if the answer has meaningful content
 */
export const hasMeaningfulValue = (questionData) => {
  if (!questionData || questionData.value === null || questionData.value === undefined) {
    return false;
  }

  const { value } = questionData;

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return value > 0;
  }

  if (typeof value === 'boolean') {
    return true; // Any boolean answer is meaningful
  }

  return true;
};

/**
 * Filters an array of optional questions to only include those that have been answered.
 * 
 * @param {Array} optionals - Array of optional question objects
 * @returns {Array} Filtered array of answered questions
 */
export const filterAnsweredQuestions = (optionals) => {
  if (!optionals || !Array.isArray(optionals)) return [];

  return optionals.filter((optional) => {
    return isQuestionAnswered(optional);
  });
};

/**
 * Filters an object of optional questions to only include those that have been answered.
 * 
 * @param {Object} optionalsObject - Object with question IDs as keys and question data as values
 * @returns {Object} Filtered object with only answered questions
 */
export const filterAnsweredQuestionsObject = (optionalsObject) => {
  if (!optionalsObject || typeof optionalsObject !== 'object') return {};

  const filtered = {};
  
  Object.keys(optionalsObject).forEach(key => {
    const questionData = optionalsObject[key];
    
    if (isQuestionAnswered(questionData)) {
      filtered[key] = questionData;
    }
  });

  return filtered;
};
